import React from 'react';
import { Link } from 'react-router-dom';
import {
  LuMail,
  LuBriefcase,
  LuMessageCircle,
  LuChartPie,
  LuFileText,
  LuShoppingBag,
  LuGlobe,
  LuClock,
  LuShield,
  LuBot
} from 'react-icons/lu';

const clients = [
  {
    name: "Hearty Mart",
    person: "Vajirali Momin",
    designation: "Managing Director & CEO",
    industry: "Retail Chain / Supermarket",
    about: "Retail supermarket chain focused on rural & semi-urban markets.",
    website: "https://heartymart.com/",
    linkedin: "https://www.linkedin.com/search/results/all/?keywords=Vajirali%20Momin%20Hearty%20Mart"
  },
  {
    name: "Garuda Aerospace",
    person: "Agnishwar Jayaprakash",
    designation: "Founder & CEO",
    industry: "Drone Manufacturing",
    about: "Drone design & manufacturing startup based in Chennai; one of India's largest drone makers.",
    website: "http://www.garudaaerospace.com/",
    linkedin: "https://in.linkedin.com/in/agnishwarjayaprakash"
  },
  {
    name: "Square Yards",
    person: "Tanuj Shori",
    designation: "Founder & CEO",
    industry: "Proptech & Fintech",
    about: "Leading real estate and fintech solutions provider.",
    website: "https://www.squareyards.com/",
    linkedin: "https://www.linkedin.com/in/tanujshori/"
  },
  {
    name: "TripJack",
    person: "Hussain Patel / Hasan Patel",
    designation: "Founders",
    industry: "Travel Technology",
    about: "Travel API integrations and booking automation powering agent systems nationwide.",
    website: "https://tripjack.com/",
    linkedin: "https://www.linkedin.com/company/tripjack/"
  }
];

const Home: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden bg-white">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-8 items-stretch">
            {/* Left Content */}
            <div className="w-full lg:w-[55%] flex flex-col justify-center py-2 lg:py-4 pr-0 lg:pr-8">
              <h1 className="text-[2.75rem] sm:text-5xl lg:text-[3.25rem] xl:text-[3.8rem] font-bold text-[#0B1538] leading-[1.15] mb-6 tracking-tight">
                We build your digital
                <br className="hidden sm:block" /> presence, so you can
                <br className="hidden sm:block" /> build your business.
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-lg font-light leading-snug">
                Build business continuity with<br className="hidden md:block"/> technology and design that deliver.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="px-6 py-3.5 bg-[#0B25E6] text-white font-semibold text-center hover:bg-blue-800 transition-colors rounded"
                >
                  Start a Free Consultation
                </Link>
                <Link
                  to="/services"
                  className="px-6 py-3.5 bg-[#E8EEFF] text-[#0B25E6] font-semibold text-center hover:bg-blue-100 transition-colors rounded"
                >
                  Explore Services
                </Link>
              </div>
            </div>

            {/* Right Side Image with Slant */}
            <div className="hidden lg:block lg:w-[55%] relative min-h-[520px]">
              <div 
                className="absolute inset-0 w-full h-full"
                style={{ clipPath: 'polygon(15% 0, 100% 0, 100% 100%, 0% 100%)' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Team working on technology and digital presence" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="pt-20 pb-12 lg:pt-32 lg:pb-16 bg-white border-t border-b border-gray-100 mt-8">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12 w-full lg:px-12">
            {/* Stat 1 */}
            <div className="w-auto pl-6 border-l-2 border-gray-200">
              <div className="flex items-baseline mb-1">
                <h3 className="text-4xl lg:text-5xl font-bold text-[#0B1538] leading-none tracking-tight">75</h3>
                <span className="text-xl lg:text-2xl font-bold text-[#0B1538] leading-none ml-1">%</span>
              </div>
              <p className="text-gray-600 text-sm lg:text-base font-medium">Of Clients Come Through Referrals</p>
            </div>

            {/* Stat 2 */}
            <div className="w-auto pl-6 border-l-2 border-gray-200">
              <div className="flex items-baseline mb-1">
                <h3 className="text-4xl lg:text-5xl font-bold text-[#0B1538] leading-none tracking-tight">100</h3>
                <span className="text-xl lg:text-2xl font-bold text-[#0B1538] leading-none ml-1">+</span>
              </div>
              <p className="text-gray-600 text-sm lg:text-base font-medium">Business Solutions Delivered</p>
            </div>

            {/* Stat 3 */}
            <div className="w-auto pl-6 border-l-2 border-gray-200">
              <div className="flex items-baseline mb-1">
                <h3 className="text-4xl lg:text-5xl font-bold text-[#0B1538] leading-none tracking-tight">10</h3>
                <span className="text-xl lg:text-2xl font-bold text-[#0B1538] leading-none ml-1">+</span>
              </div>
              <p className="text-gray-600 text-sm lg:text-base font-medium">Industries Served</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Banner */}
          <div className="bg-[#1C1C1E] rounded-[2rem] overflow-hidden relative p-12 lg:px-24 lg:py-24 text-left flex items-center mb-20 lg:mb-24 min-h-[400px]">
            
            <div className="relative z-10 w-full">
              <div className="inline-block bg-[#2A2A2E] text-gray-300 text-xs font-bold px-4 py-2 rounded tracking-[0.15em] mb-12 lg:mb-16">
                WHAT WE DO
              </div>
              <h2 className="text-3xl md:text-5xl lg:text-[3.25rem] font-bold text-white leading-[1.15] tracking-tight">
                Simplifying digital growth<br className="hidden md:block"/>
                for modern businesses.
              </h2>
            </div>
            
            {/* Dots Pattern */}
            <svg width="240" height="240" viewBox="0 0 240 240" className="absolute right-12 lg:right-24 xl:right-32 top-1/2 -translate-y-1/2 opacity-70 hidden lg:block" xmlns="http://www.w3.org/2000/svg">
              {Array.from({ length: 15 }).map((_, row) => 
                Array.from({ length: 15 }).map((_, col) => {
                  const center = 7;
                  const isVisible = Math.abs(col - center) <= row && (row + col) % 2 === 0 && row < 12;
                  return isVisible ? (
                    <rect key={`${row}-${col}`} x={col * 16 + 10} y={row * 16 + 20} width="5.5" height="5.5" fill="#E8EEFF" transform={`rotate(45 ${col * 16 + 12.75} ${row * 16 + 22.75})`} />
                  ) : null;
                })
              )}
            </svg>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {/* Strategic Design */}
            <div className="flex flex-col items-start pr-4">
              <div className="w-16 h-16 mb-4 lg:mb-6">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 50 C28 55 42 55 50 50" stroke="#0B1538" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="24" cy="24" r="14" fill="white" stroke="#0B1538" strokeWidth="2"/>
                  <text x="24" y="29" fontSize="14" fontWeight="bold" fill="#0B1538" textAnchor="middle">$</text>
                  <path d="M12 28 L 6 38 L 18 38 Z" fill="#0B25E6"/>
                  <path d="M10 14 L 10 32 L 14 32 L 14 14 Z" fill="#0B25E6"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#0B1538] mb-4">Strategic Design</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Design choices should do more than look good. Each website, logo, and visual element is created to support your growth and make it easier for people to connect with your business.
              </p>
            </div>

            {/* Smart Development */}
            <div className="flex flex-col items-start pr-4">
              <div className="w-16 h-16 mb-4 lg:mb-6">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M36 50 L32 50 C20 50 16 42 16 32 C16 20 24 14 36 14 C48 14 54 22 54 32" stroke="#0B1538" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M16 32 L26 32 M16 40 L24 40" stroke="#0B1538" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M38 38 C32 38 30 32 30 26 C30 22 34 18 38 18 C44 18 46 22 46 26 C46 32 42 38 38 38 Z" fill="white" stroke="#0B25E6" strokeWidth="2"/>
                  <path d="M36 38 L36 44 L40 44 L40 38" fill="#0B25E6"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#0B1538] mb-4">Smart Development</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Your digital foundation needs to be strong. The sites and systems we build are fast, reliable, and prepared to grow with you as your business expands.
              </p>
            </div>

            {/* Industry Insight */}
            <div className="flex flex-col items-start pr-4">
              <div className="w-16 h-16 mb-4 lg:mb-6">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M36 50 L32 50 C20 50 16 42 16 32 C16 20 24 14 36 14 C48 14 54 22 54 32" stroke="#0B1538" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M16 32 L26 32 M16 40 L24 40" stroke="#0B1538" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M36 22 L36 42" stroke="#0B25E6" strokeWidth="2"/>
                  <path d="M36 22 C42 18 48 18 48 22 L48 42 C48 38 42 38 36 42" fill="#0B25E6" stroke="#0B25E6" strokeWidth="2"/>
                  <path d="M36 22 C30 18 24 18 24 22 L24 42 C24 38 30 38 36 42" fill="none" stroke="#0B25E6" strokeWidth="2"/>
                  <path d="M39 28 L45 28 M39 33 L45 33" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#0B1538] mb-4">Industry Insight</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Every industry works differently. Understanding those differences helps us shape solutions that match your market, your customers, and the way you operate.
              </p>
            </div>

            {/* Long Term Value */}
            <div className="flex flex-col items-start pr-4">
              <div className="w-16 h-16 mb-4 lg:mb-6">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="14" y="24" width="20" height="20" fill="white" stroke="#0B1538" strokeWidth="2"/>
                  <path d="M34 24 L48 14 L48 34 L34 44" fill="#0B25E6"/>
                  <path d="M14 24 L28 14 L48 14 L34 24 Z" fill="none" stroke="#0B1538" strokeWidth="2"/>
                  <path d="M34 24 L34 44" stroke="#0B1538" strokeWidth="2"/>
                  <path d="M14 34 L28 24 L48 24" stroke="#0B1538" strokeWidth="1" strokeDasharray="3 3"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#0B1538] mb-4">Long Term Value</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                A project does not end at delivery. Staying involved, improving what is built, and helping your digital presence stay strong is part of the relationship we focus on.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1538] mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive AI solutions to streamline your business operations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Website Development',
                desc: 'Build scalable, secure, and high-performance web applications tailored to your business needs.',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />,
                color: 'primary',
                path: '/services/web-development'
              },
              {
                title: 'Mobile Application Development',
                desc: 'Create engaging and intuitive mobile experiences for iOS and Android platforms.',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />,
                color: 'secondary',
                path: '/services/mobile-development'
              },
              {
                title: 'UI/UX Design',
                desc: 'Design user-centric interfaces that deliver exceptional and delightful digital experiences.',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />,
                color: 'dark',
                path: '/services/ui-ux-design'
              },
              {
                title: 'AI & Automation Solutions',
                desc: 'Streamline operations with intelligent AI and RPA solutions tailored for enterprise workflows.',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />,
                color: 'primary',
                path: '/services/ai-automation'
              },
              {
                title: 'SaaS Product Development',
                desc: 'End-to-end development of robust SaaS platforms with multi-tenancy and subscription management.',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />,
                color: 'secondary',
                path: '/services/saas-development'
              },
              {
                title: 'Custom Software Development',
                desc: 'Bespoke software solutions built from the ground up to solve your unique business challenges.',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />,
                color: 'dark',
                path: '/services/custom-software'
              },
              {
                title: 'Cloud Infrastructure & DevOps Services',
                desc: 'Secure, scalable cloud deployments and automated CI/CD pipelines for continuous delivery.',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />,
                color: 'primary',
                path: '/services/cloud-devops'
              },
              {
                title: 'IT Support & Maintenance',
                desc: 'Reliable 24/7 technical support and proactive maintenance to keep your systems running smoothly.',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />,
                color: 'secondary',
                path: '/services/it-support'
              }
            ].map((service, index) => {
              const bgClass = service.color === 'primary' ? 'bg-primary' : service.color === 'secondary' ? 'bg-secondary' : 'bg-dark';
              const textHoverClass = service.color === 'primary' ? 'group-hover:text-primary' : service.color === 'secondary' ? 'group-hover:text-secondary' : 'group-hover:text-dark';
              const shadowClass = service.color === 'primary' ? 'shadow-primary/20' : service.color === 'secondary' ? 'shadow-secondary/20' : 'shadow-dark/20';
              const gradientClass = service.color === 'primary' ? 'from-primary/5' : service.color === 'secondary' ? 'from-secondary/5' : 'from-dark/5';

              return (
                <div key={index} className="group relative text-center p-6 rounded-2xl border border-gray-200 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden bg-white">
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradientClass} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                  <div className="relative z-10 h-full flex flex-col">
                    <div className={`w-16 h-16 ${bgClass} rounded-2xl flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-105 shadow-lg ${shadowClass}`}>
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {service.icon}
                      </svg>
                    </div>
                    <h3 className={`text-xl font-semibold text-[#0B1538] mb-3 ${textHoverClass} transition-colors duration-300`}>{service.title}</h3>
                    <p className="text-gray-600 mb-4 flex-grow">
                      {service.desc}
                    </p>
                    <Link to={service.path} className="text-[#0B25E6] hover:text-blue-800 font-medium inline-block mt-auto">
                      Learn More &rarr;
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Service: Autonomous Agents */}
      <section className="py-24 bg-white border-t border-slate-200">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 text-blue-600 font-bold tracking-wider text-sm mb-6 uppercase">
              <span>The Future Workforce</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Deploy Your <span className="text-blue-600">24/7 AI Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
              Empowering Indian enterprises with autonomous intelligent agents that handle complex workflows from Sales to HR.
            </p>

            {/* Agent Roles Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 text-left mb-20">
              {[
                { icon: LuMail, title: "AI Lead Management", desc: "Auto-capture, qualify, and follow up leads instantly." },
                { icon: LuBriefcase, title: "Sales & CRM Agent", desc: "Update CRM, send proposals, and notify sales teams." },
                { icon: LuMessageCircle, title: "WhatsApp Bot", desc: "Respond, book appointments, and sync data 24/7." },
                { icon: LuChartPie, title: "Reporting Agent", desc: "Auto-generate daily reports and insights." },
                { icon: LuFileText, title: "HR Automation", desc: "Resume screening and onboarding workflows." },
                { icon: LuShoppingBag, title: "E-commerce Agent", desc: "Order updates and product recommendations." }
              ].map((item, i) => (
                <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-4">
                    {React.createElement(item.icon as any, { className: "w-6 h-6" })}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Regional Focus */}
            <div className="grid md:grid-cols-3 gap-8 border-t border-gray-200 pt-16">
              {[
                { icon: LuGlobe, title: "Multilingual Support", desc: "Support for Indian languages & English." },
                { icon: LuClock, title: "Timezone Aligned", desc: "Optimized for Indian business hours." },
                { icon: LuShield, title: "Data Sovereignty", desc: "Compliant with local regulations." }
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-white border border-gray-200 rounded-full flex items-center justify-center mb-4 shadow-sm">
                    {React.createElement(item.icon as any, { className: "w-8 h-8 text-blue-600" })}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center">
              <Link to="/autonomous-agents" className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-bold shadow-lg gap-2">
                View All Digital Workers
                {React.createElement(LuBot as any, { className: "w-5 h-5" })}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-5 bg-light">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Tailored AI solutions for your specific industry needs
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 mb-10 gap-8">
            {[
              { name: 'E-commerce', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' },
              { name: 'Healthcare', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
              { name: 'Finance', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
              { name: 'Education', icon: 'M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z' },
              { name: 'Real Estate', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
              { name: 'Manufacturing', icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z' },
              { name: 'Retail', icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z' },
              { name: 'Travel', icon: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z' }
            ].map((industry) => (
              <div key={industry.name} className="text-center group">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md group-hover:shadow-lg group-hover:scale-105 group-hover:bg-primary transition-all duration-300">
                  <svg className="w-8 h-8 text-primary group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={industry.icon} />
                  </svg>
                </div>
                <p className="text-gray-700 font-medium group-hover:text-primary transition-colors duration-300">{industry.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why icubechat */}
      <section className="py-20 bg-white">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Why Choose icubechat
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group relative flex items-start space-x-4 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-primary/20">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 group-hover:text-primary transition-colors duration-300">Expert Team</h3>
                  <p className="text-gray-600">AI specialists with years of experience in business automation</p>
                </div>
              </div>
            </div>

            <div className="group relative flex items-start space-x-4 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 flex items-start space-x-4">
                <div className="w-12 h-12 bg-secondary rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-secondary/20">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 group-hover:text-secondary transition-colors duration-300">Fast Implementation</h3>
                  <p className="text-gray-600">Quick deployment with minimal disruption to your operations</p>
                </div>
              </div>
            </div>

            <div className="group relative flex items-start space-x-4 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-dark/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 flex items-start space-x-4">
                <div className="w-12 h-12 bg-dark rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-dark/20">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 group-hover:text-dark transition-colors duration-300">Custom Solutions</h3>
                  <p className="text-gray-600">Tailored AI solutions designed for your specific business needs</p>
                </div>
              </div>
            </div>

            <div className="group relative flex items-start space-x-4 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-primary/20">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 group-hover:text-primary transition-colors duration-300">Secure & Reliable</h3>
                  <p className="text-gray-600">Enterprise-grade security with 99.9% uptime guarantee</p>
                </div>
              </div>
            </div>

            <div className="group relative flex items-start space-x-4 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 flex items-start space-x-4">
                <div className="w-12 h-12 bg-secondary rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-secondary/20">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 group-hover:text-secondary transition-colors duration-300">24/7 Support</h3>
                  <p className="text-gray-600">Round-the-clock support to ensure smooth operations</p>
                </div>
              </div>
            </div>

            <div className="group relative flex items-start space-x-4 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-dark/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 flex items-start space-x-4">
                <div className="w-12 h-12 bg-dark rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-dark/20">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 group-hover:text-dark transition-colors duration-300">Cost Effective</h3>
                  <p className="text-gray-600">Affordable solutions with clear ROI and flexible pricing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Trust Section */}
      <section className="py-20 bg-light">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Trusted by Leading Companies
            </h2>
          </div>

          <div className="relative overflow-hidden w-full">
            <div className="flex animate-marquee space-x-8 w-max hover:[animation-play-state:paused]">
              {[...clients, ...clients].map((client, index) => (
                <a
                  key={index}
                  href={client.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 min-w-[300px] max-w-[300px] flex flex-col hover:shadow-md transition-shadow group/card"
                >
                  <div className="mb-4">
                    <div className="flex justify-between items-start">
                      <h3 className="text-xl font-bold text-primary group-hover/card:text-secondary transition-colors">{client.name}</h3>
                      <svg className="w-5 h-5 text-gray-400 group-hover/card:text-secondary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-secondary">{client.industry}</p>
                  </div>
                  <div className="mt-auto">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-semibold text-gray-800">{client.person}</p>
                      <a
                        href={client.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-gray-400 hover:text-[#0077b5] transition-colors"
                        aria-label={`${client.person} LinkedIn`}
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                        </svg>
                      </a>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{client.designation}</p>
                    <p className="text-xs text-gray-600 line-clamp-3">{client.about}</p>
                  </div>
                </a>
              ))}
            </div>
            {/* Gradient overlays for smooth fade effect */}
            <div className="absolute top-0 left-0 w-20 h-full bg-gradient-to-r from-light to-transparent z-10 pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-20 h-full bg-gradient-to-l from-light to-transparent z-10 pointer-events-none"></div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-white border-t border-gray-100 mt-10">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0B1538] mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's discuss how our AI solutions can help you grow your business and streamline operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-[#0B25E6] text-white rounded-lg hover:bg-blue-800 transition-colors text-lg font-semibold"
            >
              Book a Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
